import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components/macro';

import stringToSlug from 'helpers/stringToSlug';
import modularScale from 'styles/helpers/modular-scale';

import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import ActiveFilters from 'components/base/ActiveFilters/ActiveFilters';
import { ComponentTitle } from 'styles/global/typography/typography';
import Layout from 'components/styled/Layout/Layout';
import LoadingBar from 'components/styled/LoadingBar/LoadingBar';
import ToolbarDropdown from 'components/utility/GenericComponent/ToolbarDropdown/ToolbarDropdown';
import ToolbarOptions from 'components/utility/GenericComponent/ToolbarOptions/ToolbarOptions';
import Modal, { ModalContent } from '../Modal/Modal';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import DSPSelector from 'components/Selector/DSPSelector/DSPSelector';
import SourceOfStreamsSelector from 'components/Selector/SourceOfStreamsSelector/SourceOfStreamsSelector';
import Box from 'components/elements/Box';
import CustomizedViewButton from 'components/base/CustomizedViewButton';
import CustomizeViewDropdown from './CustomizedViewDropdown';
import Search from 'components/PlaylistActivityTracker/TracksImWatching/Search';
import PlaylistSearch from 'components/PlaylistActivityTracker/PlaylistsImWatching/PlaylistSearch/PlaylistSearch';

import { InfoIcon } from '../../data-containers/PlaylistPriorityActivity/styles';

import { TopicFilterNames } from 'components/TopicFilters/shared';
import { ThemeProps } from 'types/styled';
import { GreenButton } from 'components/PlaylistActivityTracker/styles';

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

export const WhiteBlock = styled.section<ThemeProps<'section'>>`
  background: ${({ error, theme }): string => (error ? theme.Sand : 'white')};
  padding-bottom: ${modularScale(4)};
  overflow: visible;
  margin: ${({ short }): string => (short ? '0 auto' : '1em auto')};
  width: ${({ isNarrowScreen }): string => (isNarrowScreen ? 'auto' : '75%')};
  @media screen and (min-width: 50em) {
    width: 90%;
  }
  @media screen and (min-width: 80em) {
    width: 75%;
  }
  @media screen and (max-width: 50em) {
    margin: ${({ short }): string => (short ? '0 0.5em' : '0.5em')};
    width: calc(100% - 2 * 0.5em);
  }
  ${({ isTabletNarrow }): any =>
    isTabletNarrow &&
    css`
      @media screen and (min-width: 40em) and (max-width: 50em) {
        margin: 0 auto;
        width: calc(100% - 2 * 1em);
      }
    `}
  ${ComponentTitle} {
    font-size: 20px;
    @media screen and (min-width: 50em) {
      font-size: 24px;
    }
  }
`;

export const Toolbar = styled.div<any>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 2em 0 1em
    ${({ browseRelated }): string => (browseRelated ? '!important' : '')};
  ${({ playlistActivity }): any =>
    playlistActivity &&
    css`
      @media screen and (max-width: 40em) {
        flex-wrap: wrap;
      }
    `}
`;

export const ToolbarMenu = styled('div')<any>`
  margin-left: auto;
  flex-shrink: 0;
  position: relative;
  ${({ playlistActivity }): any =>
    playlistActivity &&
    css`
      @media screen and (max-width: 40em) {
        margin-left: initial;
        width: 100%;

        ${GreenButton} {
          width: 100%;
        }
      }
    `}
`;

export const LoadingBarWrapper = styled('div')<any>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 2px;
`;

/* Needed to remain flexbox alignment */
const TitleWrapper = styled('div')``;

export const WarningMessage = styled('p')`
  font-size: ${modularScale(-1)};
  color: ${({ theme }): string => theme.CasperGrey};

  a {
    color: ${({ theme }): string => theme.CasperGrey};
    text-decoration: underline;

    &:hover,
    &:focus {
      color: ${({ theme }): string => theme.NightBlue};
    }
  }
`;

const ComponentFilters = styled(ActiveFilters)<any>`
  margin: ${({ theme }): string =>
    `${theme.spacing.xxsmall} 0 ${theme.spacing.small} 0`};
  color: ${({ theme }): string => theme.DeepBlue};
  font-size: ${({ isSmallScreen }): string =>
    isSmallScreen ? '11px' : modularScale(-1)};
`;

const StyledModal = styled(Modal)<ThemeProps<'div'>>`
  padding-bottom: 0;

  @media screen and (min-width: 50em) {
    padding-bottom: ${({ theme }): string => theme.spacing.huge};
  }

  ${ModalContent} {
    max-width: 60em;
  }
`;

const StyledErrorBoundary = styled(ErrorBoundary)`
  padding: 1em;

  @media screen and (min-width: 60em) {
    padding: ${({ theme }): string =>
      `${theme.spacing.xlarge} ${theme.spacing.huge} ${theme.spacing.xxlarge}`};
  }
`;

export interface IToolbarItem {
  to?: string | any;
  href?: string;
  label: string;
  otherProps?: any;
  onClick?: () => void;
  order?: number;
  type?: IToolbarItemType;
}

export enum IToolbarItemType {
  Export = 'Export',
  Glossary = 'Glossary',
  Edit = 'Edit',
}

export interface ICustomizedModeType {
  isShownCustomizeButton: boolean;
  onClick: () => void;
  customizeButtonText: string;
  isCustomizedActive: boolean;
  saveToApi?: any;
  formatItems?: any;
  savedItems?: any;
  allAvailableItems?: any;
  displayBlockLabel?: any;
  orderBlockLabel?: any;
  canTurnOffAll?: any;
  isCustomizedDropdownShown?: boolean;
  minWidth?: string;
}

interface ITracksImWatchingSearchProps {
  addedTracks: { id: string; type: 'track' }[];
  addTrackToCustomList: Function;
  deleteTrack: Function;
  addingTrack: string | undefined;
  deletingTrack?: string;
  removingPlaylist?: string | number;
  deletePlaylist?: (playlistId: any) => void;
}

interface IPlaylistImWatchingSearchProps {
  addedPlaylists: string[];
  addPlaylistToUserList: Function;
  customers: any[];
  addingPlaylist: string | undefined;
}

interface IGenericComponentProps {
  id?: string;
  /** Use the children props to pass an SVG icon or a <span>title</span>. */
  children: ReactNode;
  /** The title of this specific block. Can be hardcoded. */
  title: string;
  /** optional subtitle component, rendered underneath the title */
  subtitle?: any;
  /** Should we show a loading state? */
  loading: boolean;
  /** Was the load succesful? */
  success?: boolean;
  /** Should we show an error state? */
  error?: boolean | Error;
  /** Indicates that the  Toolbar Dropdown is shown */
  showToolbarDropdown?: boolean;
  /** New design instead of the toolbar dropdown */
  showToolbarOptions?: boolean;
  /** Array of configuration objects for the Toolbar options */
  extraToolbarItems?: IToolbarItem[];
  /** The glossary slug in the API */
  glossarySlug?: string;
  /** A list of filters that may be applied in thise component. Eg. ['country','streaming'] */
  allowedFilters?: TopicFilterNames[];
  /** Should it contain a DSP selector modal? */
  hasSelector?: boolean;
  /** Should we show the DSP selector modal? */
  showSelector?: boolean;
  /** Function to toggle the DSP selecotr modal */
  toggleSelector?: () => void;
  /** This component could appear different than expected, if there is a warning it will show a message */
  warning?: boolean;
  /** A custom text to append to the filter type in the ActiveFilters component */
  customFilterTooltipText?: string;
  /** if true, then without vertical margin */
  short?: boolean;
  isRedesignVersion?: boolean;
  isSmallScreen?: boolean;
  isNarrowScreen?: boolean;
  isWide?: boolean;
  /** To align the width of BrowseEntities and BrowseRelated components */
  isTabletNarrow?: boolean;
  browseRelated?: boolean;
  /** The attribute data-pw */
  dataCy?: string;
  /** css styles for browse tables */
  isNewDesign?: boolean;
  selectorType?: string;
  defaultTypes?: any;
  additionalType?: boolean | string;
  topDSPsByCountries?: any;
  isFullScreenModal?: boolean;
  customizedMode?: ICustomizedModeType;
  exportId?: string;
  playlistActivity?: {
    type: 'tracks' | 'playlists';
    data: ITracksImWatchingSearchProps | IPlaylistImWatchingSearchProps;
  };
  showIconInTitle?: boolean;
}

/**
 * The Generic Component is used as abstraction for styling, data
 * and error display and layouting of data components.
 */
const GenericComponent: React.FC<IGenericComponentProps> = (props) => {
  const {
    children,
    extraToolbarItems,
    loading,
    error,
    title,
    subtitle,
    showToolbarDropdown = true,
    showToolbarOptions = false,
    glossarySlug,
    allowedFilters,
    hasSelector,
    showSelector,
    toggleSelector,
    warning,
    short,
    isSmallScreen,
    isRedesignVersion = false,
    isNarrowScreen,
    isWide,
    isTabletNarrow,
    browseRelated,
    dataCy,
    isNewDesign,
    selectorType,
    defaultTypes,
    additionalType,
    topDSPsByCountries,
    isFullScreenModal,
    customizedMode,
    exportId,
    playlistActivity,
    showIconInTitle,
    ...attributes
  } = props;

  const tooltipPlacement = !isSmallScreen ? 'right' : 'bottom';
  const tooltipMaxWidth = !isSmallScreen ? '570px' : '350px';
  const tooltipContent =
    (title === 'Playlists' &&
      'This table includes all available playlists (~30,000+) and is refreshed every 24-48 hours.') ||
    (title === 'Priority Playlist Activity' &&
      'Priority Playlists have high follower count, high stream count and/or high business value. They are curated by business stakeholders.') ||
    '';
  const marginRight =
    ((title === 'Playlists' || title === 'Priority Playlist Activity') &&
      '0') ||
    '1em';

  return (
    <WhiteBlock
      error={error ? true : false}
      isLoading={loading}
      {...attributes}
      data-pw={dataCy || stringToSlug(title)}
      isSmallScreen={isSmallScreen}
      short={short}
      isNarrowScreen={isNarrowScreen}
      isTabletNarrow={isTabletNarrow}
    >
      <LoadingBarWrapper isSmallScreen={isSmallScreen}>
        <LoadingBar loading={loading} />
      </LoadingBarWrapper>

      <Layout.MaxWidth
        isSmallScreen={isSmallScreen}
        isNarrowScreen={isNarrowScreen}
        isWide={isWide}
        browseRelated={browseRelated}
        isNewDesign={isNewDesign}
        id={exportId}
      >
        <Toolbar
          browseRelated={browseRelated}
          playlistActivity={playlistActivity}
        >
          <TitleWrapper>
            <Box display="flex" alignItems="baseline">
              <ComponentTitle
                isSmallScreen={isSmallScreen}
                marginRigth={marginRight}
              >
                {title}
              </ComponentTitle>

              {showIconInTitle && (
                <Box width="0.875rem" height="0.875rem" ml={2}>
                  <TippyToolTip
                    tooltipContent={tooltipContent}
                    placement={tooltipPlacement}
                    maxWidth={tooltipMaxWidth}
                    offset={[0, 15]}
                    enabled
                    isAligned
                  >
                    <InfoIcon />
                  </TippyToolTip>
                </Box>
              )}
            </Box>
            {warning && (
              <WarningMessage>
                The selected DSPs may be different than expected, as your
                personal settings failed to load. Please refresh the page or{' '}
                <a href="mailto:opus-support@wmg.com">contact Opus Support</a>.
              </WarningMessage>
            )}
            {subtitle && <Box>{subtitle}</Box>}
            {allowedFilters && (
              <ComponentFilters
                allowedFilters={allowedFilters}
                isSmallScreen={isSmallScreen}
                newDesign={browseRelated}
              />
            )}
          </TitleWrapper>
          <ToolbarMenu
            isOldVersion={!isRedesignVersion}
            data-gtag="context-menu"
            playlistActivity={playlistActivity}
          >
            <Box display="flex">
              <CustomizedViewButton
                customizedMode={customizedMode}
                isSmallScreen={isSmallScreen}
              />
              {showToolbarDropdown && (
                <ToolbarDropdown
                  glossarySlug={glossarySlug}
                  extraToolbarItems={extraToolbarItems}
                  title={title}
                />
              )}
              {showToolbarOptions && (
                <ToolbarOptions
                  glossarySlug={glossarySlug}
                  extraToolbarItems={extraToolbarItems}
                  title={title}
                />
              )}
            </Box>
            {customizedMode?.isCustomizedDropdownShown && (
              <CustomizeViewDropdown
                {...customizedMode}
                saveToApi={customizedMode?.saveToApi || noop}
              />
            )}
            {playlistActivity &&
              (playlistActivity.type === 'tracks' ? (
                <Search
                  {...playlistActivity.data}
                  isSmallScreen={isSmallScreen}
                  isSeparate={false}
                />
              ) : (
                <PlaylistSearch
                  {...playlistActivity.data}
                  isSmallScreen={isSmallScreen}
                  isSeparate={false}
                />
              ))}
          </ToolbarMenu>
        </Toolbar>
        {children}
        {hasSelector && showSelector && (
          <StyledModal hide={toggleSelector} isFullScreen={isFullScreenModal}>
            <StyledErrorBoundary>
              {selectorType === 'SourceOfStreams' ? (
                <SourceOfStreamsSelector
                  toggleSelector={toggleSelector}
                  defaultTypes={defaultTypes}
                />
              ) : (
                <DSPSelector
                  topDSPsByCountries={topDSPsByCountries}
                  toggleDSPSelector={toggleSelector}
                  additionalType={additionalType}
                />
              )}
            </StyledErrorBoundary>
          </StyledModal>
        )}
      </Layout.MaxWidth>
    </WhiteBlock>
  );
};

export default GenericComponent;
